import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useIsInWishlist } from '@/lib/wishlistUtils';
import { AuthResponse } from '@/types/schema';

interface WishlistButtonProps {
  propertyId: number;
  className?: string;
  showText?: boolean;
}

const WishlistButton: React.FC<WishlistButtonProps> = ({ 
  propertyId, 
  className = '',
  showText = false 
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Get authentication status
  const { data: authData } = useQuery<AuthResponse>({
    queryKey: ['/api/auth/me'],
    retry: false,
    refetchOnWindowFocus: false,
  });
  
  const isAuthenticated = !!authData?.user;
  
  // Check if property is in wishlist
  const { isInWishlist, isLoading } = useIsInWishlist(propertyId);

  // Add to wishlist mutation
  const addToWishlistMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', `/api/wishlist/${propertyId}`, {});
    },
    onSuccess: () => {
      // Show notification
      document.dispatchEvent(new CustomEvent('wishlist:added', { 
        detail: { propertyId } 
      }));
      
      // Invalidate wishlist query
      queryClient.invalidateQueries({ queryKey: ['/api/wishlist'] });
      
      toast({
        title: "Auf die Wunschliste gesetzt",
        description: "Die Immobilie wurde zu Ihrer Wunschliste hinzugefügt.",
      });
    },
    onError: (error) => {
      toast({
        title: "Fehler",
        description: "Die Immobilie konnte nicht zur Wunschliste hinzugefügt werden.",
        variant: "destructive",
      });
    }
  });

  // Remove from wishlist mutation
  const removeFromWishlistMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('DELETE', `/api/wishlist/${propertyId}`, {});
    },
    onSuccess: () => {
      // Invalidate wishlist query
      queryClient.invalidateQueries({ queryKey: ['/api/wishlist'] });
      
      toast({
        title: "Von der Wunschliste entfernt",
        description: "Die Immobilie wurde von Ihrer Wunschliste entfernt.",
      });
    },
    onError: (error) => {
      toast({
        title: "Fehler",
        description: "Die Immobilie konnte nicht von der Wunschliste entfernt werden.",
        variant: "destructive",
      });
    }
  });

  const handleWishlistClick = () => {
    if (!isAuthenticated) {
      toast({
        title: "Anmeldung erforderlich",
        description: "Bitte melden Sie sich an, um Immobilien zu Ihrer Wunschliste hinzuzufügen.",
        variant: "default",
      });
      return;
    }
    
    if (isInWishlist) {
      removeFromWishlistMutation.mutate();
    } else {
      addToWishlistMutation.mutate();
    }
  };

  return (
    <Button
      variant="outline"
      size={showText ? "default" : "icon"}
      className={`${className} hover:text-global-cyan hover:border-global-cyan ${
        isInWishlist ? 'text-global-cyan border-global-cyan' : ''
      }`}
      onClick={handleWishlistClick}
      disabled={isLoading || addToWishlistMutation.isPending || removeFromWishlistMutation.isPending}
      title={isInWishlist ? "Von der Wunschliste entfernen" : "Zur Wunschliste hinzufügen"}
    >
      <Heart className={`${showText ? 'mr-2' : ''} h-5 w-5 ${isInWishlist ? 'fill-global-cyan' : ''}`} />
      {showText && (isInWishlist ? "Von der Wunschliste entfernen" : "Zur Wunschliste hinzufügen")}
    </Button>
  );
};

export default WishlistButton;
